import React, { useState, useEffect, useCallback } from 'react'
import { Button, DataTable, Modal } from '@arnsbogroup/react-voicearchive'
import NiceModal from '@ebay/nice-modal-react'
import MemoryRequestActions from 'components/MemoryRequestActions'
import AttachMemoriesModal from 'components/AttachMemoriesModal'
import QuickCreateMemoryModal from 'components/QuickCreateMemoryModal'
import { MemoryStatusChip } from 'components/datatable'

const TABLE_HEAD = {
  text: { label: 'Memory', className: 'w-1/2' },
  language_name: { label: 'Language', className: 'w-1/6' },
  status: { label: 'Status', className: 'w-1/6' },
  audio: { label: 'Audio', className: 'w-1/6' },
}

const TABLE_BODY = {
  text: (m) => <span className="text-sm">{m.text || '-'}</span>,
  language_name: (m) => m.language_name
    ? <span className="text-sm">{m.language_name}</span>
    : <span className="text-gray-400 text-xs">-</span>,
  status: (m) => <MemoryStatusChip memory={m} />,
  audio: (m) => m.audio_stream_url ? (
    <audio
      style={{ width: '100%', minWidth: '160px', height: '25px' }}
      preload="none"
      src={m?.audio_stream_url}
      controls
      controlsList="nodownload"
    />
  ) : null,
}

const taskLabel = (task) => {
  if (!task) return null
  const type = task.type?.shortname || task.type?.name
  const user = task.supplier?.userid
  if (type && user) return "Task: " + `${type} — ${user}`
  if (type) return "Task: " + type
  return "Task: " + (task.title || `Task #${task.uid}`)
}

const versionLabel = (ver) => "Version: " + (ver?.title || `Version #${ver?.uid}`)
const jobLabel = (job) => "Job: " + (job?.title || `Job #${job?.uid}`)

/**
 * Build the ordered list of sections to fetch and display, drilling DOWN from
 * the current context level into all child entities.
 *
 * Returns an array of { label, param, linkType, linkUid } objects.
 */
const buildSections = (order, contextType, contextUid, version) => {
  const sections = []

  if (contextType === 'order') {
    sections.push({ label: 'Order Level', param: `search[orderDirectId]=${contextUid}`, linkType: 'order', linkUid: contextUid })
    for (const job of (order.tasks || [])) {
      sections.push({ label: jobLabel(job), param: `search[ordertaskId]=${job.uid}`, linkType: 'ordertask', linkUid: job.uid })
      for (const ver of (job.versions || [])) {
        sections.push({ label: versionLabel(ver), param: `search[ordertaskId]=${ver.uid}`, linkType: 'ordertask', linkUid: ver.uid })
        for (const task of (ver.tasks || [])) {
          sections.push({ label: taskLabel(task), param: `search[ordertaskId]=${task.uid}`, linkType: 'ordertask', linkUid: task.uid })
        }
      }
    }
  } else if (contextType === 'job') {
    const job = (order.tasks || []).find(j => +j.uid === +contextUid)
    sections.push({ label: job ? jobLabel(job) : `Job #${contextUid}`, param: `search[ordertaskId]=${contextUid}`, linkType: 'ordertask', linkUid: contextUid })
    for (const ver of (job?.versions || [])) {
      sections.push({ label: versionLabel(ver), param: `search[ordertaskId]=${ver.uid}`, linkType: 'ordertask', linkUid: ver.uid })
      for (const task of (ver.tasks || [])) {
        sections.push({ label: taskLabel(task), param: `search[ordertaskId]=${task.uid}`, linkType: 'ordertask', linkUid: task.uid })
      }
    }
  } else if (contextType === 'version') {
    sections.push({ label: versionLabel(version || { uid: contextUid }), param: `search[ordertaskId]=${contextUid}`, linkType: 'ordertask', linkUid: contextUid })
    for (const task of (version?.tasks || [])) {
      sections.push({ label: taskLabel(task), param: `search[ordertaskId]=${task.uid}`, linkType: 'ordertask', linkUid: task.uid })
    }
  } else {
    // task level — only this task
    const task = (version?.tasks || []).find(t => +t.uid === +contextUid)
    sections.push({ label: task ? taskLabel(task) : `Task #${contextUid}`, param: `search[ordertaskId]=${contextUid}`, linkType: 'ordertask', linkUid: contextUid })
  }

  return sections
}

/**
 * Walk the nested order task tree and build a flat taskUid→memories map from
 * memories already embedded in the order payload by includeRelationsExperimental.
 */
const buildSectionMemoriesFromOrder = (order, contextType, contextUid, version) => {
  const taskMemoriesMap = {}
  const walk = (tasks) => {
    for (const t of (tasks || [])) {
      taskMemoriesMap[t.uid] = t.memories || []
      walk(t.versions)
      walk(t.tasks)
    }
  }
  walk(order.tasks)

  const map = {}
  buildSections(order, contextType, contextUid, version).forEach(section => {
    map[section.param] = section.linkType === 'order'
      ? (order.memories || [])
      : (taskMemoriesMap[section.linkUid] || [])
  })
  return map
}

const OrderMemoriesTab = ({ order, contextType, contextUid, version = null }) => {
  const [selectedIds, setSelectedIds] = useState([])
  const [requestLinkMemoryId, setRequestLinkMemoryId] = useState(null)
  const [sectionMemories, setSectionMemories] = useState(() =>
    buildSectionMemoriesFromOrder(order, contextType, contextUid, version)
  )
  const [loading, setLoading] = useState(false)

  const isOrderContext = contextType === 'order'
  const linkType = isOrderContext ? 'order' : 'ordertask'
  const targetLanguageId = (contextType === 'version' || contextType === 'task')
    ? (version?.targetlanguageId || version?.targetlanguage || null)
    : null

  // Re-derive from order prop whenever it or the context changes.
  useEffect(() => {
    setSectionMemories(buildSectionMemoriesFromOrder(order, contextType, contextUid, version))
  }, [order, contextType, contextUid, version])

  // Used after create/attach to get fresh data from the API.
  const fetchMemories = useCallback(() => {
    setLoading(true)
    setSelectedIds([])
    const sections = buildSections(order, contextType, contextUid, version)
    Promise.all(
      sections.map(s =>
        fetch(`/rest/v2/memory?${s.param}&pageSize=100`, { credentials: 'same-origin' })
          .then(r => r.json())
          .then(({ data }) => ({ param: s.param, data: data || [] }))
      )
    ).then(results => {
      const map = {}
      results.forEach(({ param, data }) => { map[param] = data })
      setSectionMemories(map)
    }).finally(() => setLoading(false))
  }, [order, contextType, contextUid, version])

  const handleRowSelected = (uid) =>
    setSelectedIds(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid])

  const handleAttach = () => {
    NiceModal.show(AttachMemoriesModal, {
      order,
      linkType,
      linkUid: contextUid,
      targetLanguageId,
    }).then(fetchMemories)
  }

  const handleCreate = () => {
    NiceModal.show(QuickCreateMemoryModal, {
      order,
      contextType,
      contextUid,
      version,
    }).then(fetchMemories)
  }

  const sections = buildSections(order, contextType, contextUid, version)

  // Deduplicate across sections: process most-specific sections first so a
  // memory that appears at multiple levels is shown only at the deepest one.
  const seenUids = new Set()
  const dedupedSectionMemories = {}
  ;[...sections].reverse().forEach(section => {
    const unique = (sectionMemories[section.param] || []).filter(m => !seenUids.has(m.uid))
    unique.forEach(m => seenUids.add(m.uid))
    dedupedSectionMemories[section.param] = unique
  })

  const allEntries = sections.flatMap(section => {
    const memories = dedupedSectionMemories[section.param] || []
    if (memories.length === 0) return []
    return [
      { _groupHeader: true, _label: section.label, _groupIds: memories.map(m => m.uid) },
      ...memories,
    ]
  })

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={handleAttach}>
            Attach existing customer memories
          </Button>
          <Button size="sm" onClick={handleCreate}>
            Create memory
          </Button>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <MemoryRequestActions
          memoryIds={selectedIds}
          onSuccess={() => setSelectedIds([])}
        />
      )}

      <DataTable
        head={TABLE_HEAD}
        body={TABLE_BODY}
        entries={allEntries}
        rowSelectionKey="uid"
        rowsSelected={selectedIds}
        onRowSelected={handleRowSelected}
        rowActions={[
          {
            label: 'Download',
            isVisible: (m) => !!m.audio_download_url,
            onClick: (m) => window.open(m.audio_download_url, '_blank'),
          },
          {
            label: 'Request link',
            onClick: (m) => setRequestLinkMemoryId(m.uid),
          },
        ]}
        noDataMessage={loading ? 'Loading...' : 'No memories.'}
      />

      <Modal
        show={!!requestLinkMemoryId}
        title="Request link"
        onClose={() => setRequestLinkMemoryId(null)}
        onOutsideClick={() => setRequestLinkMemoryId(null)}
        dialogPanelClasses="max-w-screen-sm overflow-x-scroll"
      >
        {requestLinkMemoryId && (
          <MemoryRequestActions
            memoryIds={[requestLinkMemoryId]}
            defaultOpen
            onCancel={() => setRequestLinkMemoryId(null)}
          />
        )}
      </Modal>
    </div>
  )
}

export default OrderMemoriesTab
